import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { colors } from "../../../styles/figmaColors";
import { typographies } from "../../../styles/figmaTypographies";
import { StyledColumn, StyledFlex } from "./layout";
import { Spacer } from "../Spacer";
import { TextCapitalized } from "../text/TextCapitalized";
import { formatAmount } from "../../../format";

export type ProgressCardVariant = "grey" | "white";

export type Period = "DAILY" | "WEEKLY" | "MONTHLY";

const VARIANTS: Record<
  ProgressCardVariant,
  { card: string; track: string; border: string }
> = {
  grey: {
    card: colors["colors/surfaces/background/background_level1"],
    track: colors["colors/surfaces/background/background_level0"],
    border: "none",
  },
  white: {
    card: colors["colors/surfaces/background/background_level0"],
    track: colors["colors/borders/cards/cards"],
    border: `0.0625rem solid ${colors["colors/borders/cards/cards"]}`,
  },
};

export interface ProgressCardProps {
  period: Period;
  value: number;
  max: number;
  variant?: ProgressCardVariant;
}

export const ProgressCard = ({
  period,
  value,
  max,
  variant = "grey",
}: ProgressCardProps) => {
  const { t } = useTranslation();
  const ratio = Math.min(Math.max(value / max, 0), 1);

  return (
    <StyledCard $variant={variant}>
      <Spacer y={1.5} />
      <StyledFlex>
        <Spacer x={1.25} />
        <StyledContent>
          <StyledRow>
            <StyledLabel>
              <TextCapitalized>
                {t(`progressCard.${period.toLowerCase()}`)}
              </TextCapitalized>
            </StyledLabel>
            <StyledLabel>{`${formatAmount(value)}/${formatAmount(max)} €`}</StyledLabel>
          </StyledRow>
          <Spacer y={1.5} />
          <StyledTrack $variant={variant}>
            <StyledFill $ratio={ratio} />
          </StyledTrack>
        </StyledContent>
        <Spacer x={1.25} />
      </StyledFlex>
      <Spacer y={1.5} />
    </StyledCard>
  );
};

const StyledCard = styled(StyledColumn)<{ $variant: ProgressCardVariant }>`
  width: 100%;
  background: ${({ $variant }) => VARIANTS[$variant].card};
  border: ${({ $variant }) => VARIANTS[$variant].border};
  border-radius: 1.125rem;
`;

const StyledContent = styled(StyledColumn)`
  flex: 1;
  min-width: 0;
`;

const StyledRow = styled(StyledFlex)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledLabel = styled.div`
  ${typographies["Body/XL"]}
  color: ${colors["colors/text/black"]};
`;

const StyledTrack = styled.div<{ $variant: ProgressCardVariant }>`
  width: 100%;
  height: 0.5rem;
  border-radius: 25rem;
  background: ${({ $variant }) => VARIANTS[$variant].track};
  overflow: hidden;
`;

const StyledFill = styled.div<{ $ratio: number }>`
  height: 100%;
  border-radius: 25rem;
  background: ${colors["colors/accent/600"]};
  width: ${({ $ratio }) => $ratio * 100}%;
`;
